import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_DOCS_MD = `
# OCR Document Parsing API

Convert PDF documents and images into structured markdown with layout detection, table recognition, chart parsing, and more.

---

## Authentication

\`\`\`
Authorization: Bearer <YOUR_API_KEY>
\`\`\`

Include the API key in every request header.

---

## Getting Started

### 1. Submit a document

**POST** \`/tasks/create\`

Upload a PDF or image file to start processing.

\`\`\`bash
curl -X POST https://api.example.com/tasks/create \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -F "file=@annual_report.pdf"
\`\`\`

**Response:**
\`\`\`json
{
  "errorCode": 0,
  "result": {
    "taskId": "t-abc123def456",
    "taskStatus": 1,
    "statusText": "queued"
  }
}
\`\`\`

### 2. Check processing status

**POST** \`/tasks/batch\`

Poll every 2-3 seconds until \`taskStatus\` reaches \`5\` (completed).

\`\`\`bash
curl -X POST https://api.example.com/tasks/batch \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"taskIds": ["t-abc123def456"]}'
\`\`\`

**Response:**
\`\`\`json
{
  "errorCode": 0,
  "result": [{
    "taskId": "t-abc123def456",
    "taskStatus": 5,
    "totalPages": 11,
    "extractedPages": 11
  }]
}
\`\`\`

| Status Code | Meaning |
|------------|---------|
| 1 | Queued - waiting to be processed |
| 2 | Processing - currently being parsed |
| 5 | Completed - results ready |
| 9 | Failed - check error field for details |

### 3. Retrieve results

**GET** \`/tasks/{taskId}/detail\`

\`\`\`bash
curl https://api.example.com/tasks/t-abc123def456/detail \\
  -H "Authorization: Bearer <YOUR_API_KEY>"
\`\`\`

**Response:**
\`\`\`json
{
  "errorCode": 0,
  "result": {
    "taskId": "t-abc123def456",
    "fileName": "annual_report.pdf",
    "totalPages": 11,
    "inferenceTime": 31.5,
    "parsingResult": [
      {
        "pageIndex": 0,
        "markdown": "# Annual Report 2024\\n\\n## Financial Highlights...",
        "blocks": [
          {
            "block_label": "doc_title",
            "block_content": "# Annual Report 2024",
            "block_bbox": [131, 36, 1384, 127],
            "block_order": 1
          }
        ]
      }
    ]
  }
}
\`\`\`

### 4. Get markdown only

**GET** \`/tasks/{taskId}/markdown\`

Returns plain markdown text, convenient for downstream processing.

\`\`\`bash
curl https://api.example.com/tasks/t-abc123def456/markdown \\
  -H "Authorization: Bearer <YOUR_API_KEY>"
\`\`\`

---

## Batch Processing

Submit multiple files in a single request.

**POST** \`/tasks/batch/create\`

\`\`\`bash
curl -X POST https://api.example.com/tasks/batch/create \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -F "files=@report_q1.pdf" \\
  -F "files=@report_q2.pdf" \\
  -F "files=@invoice.png"
\`\`\`

**Response:**
\`\`\`json
{
  "errorCode": 0,
  "result": {
    "batchId": "1775200409639",
    "tasks": [
      {"taskId": "t-abc123", "fileName": "report_q1.pdf", "taskStatus": 1},
      {"taskId": "t-def456", "fileName": "report_q2.pdf", "taskStatus": 1},
      {"taskId": "t-ghi789", "fileName": "invoice.png", "taskStatus": 1}
    ],
    "totalFiles": 3
  }
}
\`\`\`

Poll all tasks at once using \`/tasks/batch\` with the array of task IDs.

---

## Parsing Options

Customize parsing behavior by including these parameters in your request.

### Recognition Features

| Parameter | Type | Default | Description |
|-----------|------|---------|-------------|
| \`useLayoutDetection\` | boolean | true | Detect document layout structure (headings, paragraphs, tables) |
| \`useChartRecognition\` | boolean | false | Parse charts and graphs into structured data |
| \`useSealRecognition\` | boolean | false | Detect and recognize seals/stamps |
| \`useOcrForImageBlock\` | boolean | false | Extract text from embedded images |
| \`layoutNms\` | boolean | true | Apply non-maximum suppression to layout detection |
| \`layoutShapeMode\` | string | "auto" | Bounding box type: \`auto\`, \`rect\`, \`quad\`, \`poly\` |

### Prompt Type

| Value | Use case |
|-------|----------|
| \`ocr\` | General text recognition (default) |
| \`formula\` | Mathematical formula recognition |
| \`table\` | Table structure recognition |
| \`chart\` | Chart/graph recognition |
| \`seal\` | Seal/stamp recognition |
| \`spotting\` | Text detection and localization |

### Model Parameters

| Parameter | Type | Default | Description |
|-----------|------|---------|-------------|
| \`repetitionPenalty\` | float | 1.0 | Reduce repeated text (0.1 - 3.0) |
| \`temperature\` | float | 0.0 | Recognition variability (0.0 - 2.0) |
| \`topP\` | float | 1.0 | Sampling threshold (0.0 - 1.0) |
| \`minPixels\` | integer | 147384 | Minimum image resolution |
| \`maxPixels\` | integer | 2822400 | Maximum image resolution |

### Processing Options

| Parameter | Type | Default | Description |
|-----------|------|---------|-------------|
| \`mergeTables\` | boolean | true | Merge tables that span multiple pages |
| \`relevelTitles\` | boolean | true | Reconstruct heading hierarchy |
| \`force\` | boolean | false | Bypass cache and re-process |

**Example with options:**

\`\`\`bash
curl -X POST https://api.example.com/tasks/create \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -F "file=@financial_report.pdf" \\
  -F "useChartRecognition=true" \\
  -F "useSealRecognition=true" \\
  -F "useOcrForImageBlock=true" \\
  -F "promptLabel=ocr"
\`\`\`

---

## Direct Parsing

For synchronous processing without the task queue.

**POST** \`/layout-parsing\`

Send a base64-encoded file and receive results immediately.

\`\`\`bash
curl -X POST https://api.example.com/layout-parsing \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "<base64-encoded-file>",
    "fileType": 0,
    "useLayoutDetection": true
  }'
\`\`\`

| fileType | Description |
|----------|-------------|
| 0 | PDF document |
| 1 | Image (PNG, JPG, BMP, WEBP) |

> **Note:** For large files or batch processing, use the task-based API instead.

---

## Response Format

### Page Object

Each page in \`parsingResult\` contains:

| Field | Type | Description |
|-------|------|-------------|
| \`pageIndex\` | integer | Zero-based page number |
| \`markdown\` | string | Rendered markdown with embedded images (base64) |
| \`blocks\` | array | Individual content blocks with labels and coordinates |
| \`layoutDetRes\` | object | Raw layout detection results |
| \`pageWidth\` | integer | Page width in pixels |
| \`pageHeight\` | integer | Page height in pixels |

### Block Object

Each block represents a detected content element:

| Field | Type | Description |
|-------|------|-------------|
| \`block_label\` | string | Content type (see table below) |
| \`block_content\` | string | Parsed content (text, HTML table, markdown heading, etc.) |
| \`block_bbox\` | array | Bounding box [x1, y1, x2, y2] |
| \`block_order\` | integer | Reading order on the page |
| \`block_polygon_points\` | array | Polygon coordinates for irregular shapes |

### Block Labels

| Label | Description |
|-------|-------------|
| \`doc_title\` | Document title |
| \`paragraph_title\` | Section heading |
| \`text\` | Body text |
| \`table\` | Table |
| \`image\` | Image |
| \`chart\` | Chart or graph |
| \`formula\` | Mathematical formula |
| \`seal\` | Seal or stamp |
| \`header\` / \`header_image\` | Page header |
| \`footer\` / \`footer_image\` | Page footer |
| \`number\` | Page number |
| \`footnote\` | Footnote |
| \`aside_text\` | Margin text |

---

## Caching

Responses are automatically cached based on file content and parsing parameters.

- **Same file + same parameters** → instant cached response
- **Same file + different parameters** → re-processed
- **\`force=true\`** → always re-process, ignoring cache
- **Cache duration:** 2 hours

---

## Cache Management

**GET** \`/cache/stats\` — View cache statistics

**POST** \`/cache/clear\` — Clear cache

\`\`\`bash
curl -X POST https://api.example.com/cache/clear \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"pattern": "cache"}'
\`\`\`

| Pattern | Clears |
|---------|--------|
| \`cache\` | File result cache only |
| \`tasks\` | All task records |
| \`batch\` | Batch records |
| \`queue\` | Processing queue |
| *(empty)* | Everything |

---

## Code Examples

### Python

\`\`\`python
import requests
import time

API_URL = "https://api.example.com"
API_KEY = "<YOUR_API_KEY>"
headers = {"Authorization": f"Bearer {API_KEY}"}


def parse_document(file_path, **options):
    """Parse a document and return structured results."""

    # Submit file
    with open(file_path, "rb") as f:
        resp = requests.post(
            f"{API_URL}/tasks/create",
            headers=headers,
            files={"file": f},
            data={
                "useLayoutDetection": "true",
                "useChartRecognition": "true",
                **{k: str(v) for k, v in options.items()},
            },
        )
    task_id = resp.json()["result"]["taskId"]

    # Wait for completion
    while True:
        r = requests.post(
            f"{API_URL}/tasks/batch",
            headers={**headers, "Content-Type": "application/json"},
            json={"taskIds": [task_id]},
        )
        status = r.json()["result"][0]["taskStatus"]
        if status == 5:
            break
        if status == 9:
            raise Exception("Processing failed")
        time.sleep(2)

    # Get results
    result = requests.get(
        f"{API_URL}/tasks/{task_id}/detail",
        headers=headers,
    ).json()["result"]

    return result


# Usage
result = parse_document("report.pdf")

print(f"Pages: {result['totalPages']}")
print(f"Processing time: {result['inferenceTime']}s")

for page in result["parsingResult"]:
    print(f"\\n--- Page {page['pageIndex'] + 1} ---")
    print(page["markdown"][:500])
\`\`\`

### JavaScript / Node.js

\`\`\`javascript
const API_URL = "https://api.example.com";
const API_KEY = "<YOUR_API_KEY>";

async function parseDocument(filePath) {
  const form = new FormData();
  form.append("file", fs.createReadStream(filePath));
  form.append("useChartRecognition", "true");

  // Submit
  const createResp = await fetch(\`\${API_URL}/tasks/create\`, {
    method: "POST",
    headers: { Authorization: \`Bearer \${API_KEY}\` },
    body: form,
  });
  const { result } = await createResp.json();
  const taskId = result.taskId;

  // Poll
  let status = 0;
  while (status < 5) {
    await new Promise((r) => setTimeout(r, 2000));
    const pollResp = await fetch(\`\${API_URL}/tasks/batch\`, {
      method: "POST",
      headers: {
        Authorization: \`Bearer \${API_KEY}\`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ taskIds: [taskId] }),
    });
    const poll = await pollResp.json();
    status = poll.result[0].taskStatus;
  }

  // Get result
  const detailResp = await fetch(
    \`\${API_URL}/tasks/\${taskId}/detail\`,
    { headers: { Authorization: \`Bearer \${API_KEY}\` } }
  );
  return (await detailResp.json()).result;
}
\`\`\`

---

## Health Check

**GET** \`/health\` — No authentication required

\`\`\`bash
curl https://api.example.com/health
\`\`\`

\`\`\`json
{
  "status": "ok",
  "redis": true,
  "upstream": true,
  "queue_length": 0,
  "max_workers": 3
}
\`\`\`

---

## Limits

| Limit | Value |
|-------|-------|
| Max file size | 50 MB |
| Supported formats | PDF, PNG, JPG, JPEG, BMP, WEBP |
| Cache TTL | 2 hours |
| Concurrent workers | 3 |
`;

export default function ApiDocsContent() {
    return (
        <div className="prose prose-sm max-w-none
            prose-headings:text-gray-900
            prose-h1:text-xl prose-h1:border-b prose-h1:border-gray-200 prose-h1:pb-2 prose-h1:mb-4
            prose-h2:text-lg prose-h2:mt-6
            prose-h3:text-base prose-h3:mt-4
            prose-h4:text-sm
            prose-p:text-gray-700
            prose-code:text-blue-700 prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs
            prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:text-xs prose-pre:rounded-lg
            prose-table:text-xs
            prose-th:bg-gray-50 prose-th:text-gray-600 prose-th:font-semibold prose-th:text-left
            prose-td:text-gray-700
            prose-strong:text-gray-900
            prose-hr:border-gray-200
            prose-blockquote:border-blue-300 prose-blockquote:bg-blue-50 prose-blockquote:text-blue-800 prose-blockquote:text-xs prose-blockquote:rounded-r-lg
        ">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {API_DOCS_MD}
            </ReactMarkdown>
        </div>
    );
}
